import { ElementRef, inject, Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SpinnerType } from '../../util/interceptors';

/**
 * Service holds the number of active requests and tells
 * if the spinner must be shown.
 *
 * Requests count in **LoadInterceptor**.
 */
@Injectable({
  providedIn: 'root',
})
export class SpinnerService implements OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();
  private templateRef!: ElementRef;
  private renderer: Renderer2;
  private router: Router = inject(Router);
  private rendererFactory: RendererFactory2 = inject(RendererFactory2);

  private activeRequests = 0;
  private spinnerActiveDivs: ElementRef[] = [];
  private isActive$ = new BehaviorSubject<boolean>(undefined);
  private spinnerText$ = new BehaviorSubject<string>('');
  private currentSpinnerType$ = new BehaviorSubject<SpinnerType>(SpinnerType.DEFAULT);
  /**
   * Tracks active request counts by spinner type.
   * Key: SpinnerType enum value (e.g., {@link SpinnerType#DEFAULT}, {@link SpinnerType#CUSTOM})
   * Value: Number of active requests for that type
   *
   * Used to determine which spinner variant to display when multiple
   * request types are active simultaneously. Non-default types take
   * priority over the default spinner.
   */
  private activeRequestsByType = new Map<SpinnerType, number>();


  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);

    this.router.events.pipe(takeUntil(this._destroyStream)).subscribe((e) => {
      /**
       * This condition is used for situations when some requests got stuck
       * and the user just trying to go to another page.
       * Without this even if the user goes to another page, this service will
       * have unresolved requests, so the spinner will be activated forever.
       */
      if (e instanceof NavigationStart) {
        this.resetActiveRequests();
      }
    });
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
  }

  addActiveRequest(text?: string, spinnerType: SpinnerType = SpinnerType.DEFAULT): void {
    this.activeRequests++;

    // Track requests by type
    const currentCount = this.activeRequestsByType.get(spinnerType) || 0;
    this.activeRequestsByType.set(spinnerType, currentCount + 1);

    if (text) {
      this.spinnerText$.next(text);
    }
    this.checkActive();
    this.currentSpinnerType$.next(spinnerType);
  }

  removeActiveRequest(spinnerType?: SpinnerType): void {
    /**
     * For situations when we changed page but there were some requests
     * on the previous page that wasn't resolved, so they are resolving
     * together with current page requests and can make this counter negative.
     */
    if (this.activeRequests - 1 >= 0) {
      this.activeRequests--;

      const currentCount = this.activeRequestsByType.get(spinnerType) || 0;
      if (currentCount > 0) {
        this.activeRequestsByType.set(spinnerType, currentCount - 1);
      }
    }

    this.checkActive();
    this.updateSpinnerType();

    if (this.activeRequests === 0) {
      this.spinnerText$.next('');
    }
  }

  setLocalSpinnerTemplate(template: ElementRef): void {
    this.templateRef = template;
  }

  startLoadingForComponent(component: ElementRef): void {
    const element: HTMLElement = component.nativeElement;
    element.setAttribute('style', 'position: relative');
    element.insertAdjacentHTML(
      'beforeend',
      this.templateRef.nativeElement.innerHTML,
    );
    this.spinnerActiveDivs.push(component);
  }

  finishLoadingForComponent(component: ElementRef): void {
    if (!component) {
      return;
    }
    this.spinnerActiveDivs.splice(this.spinnerActiveDivs.indexOf(component));
    const element = component.nativeElement;
    const loadingCompTemplate = element.querySelector('#component-spinner');
    if (!loadingCompTemplate) {
      // Spinner was finished already / did not exist.
      return;
    }
    this.renderer.removeChild(element, loadingCompTemplate);
  }

  resetActiveRequests(): void {
    this.activeRequests = 0;
    this.activeRequestsByType.clear();
    this.spinnerActiveDivs.forEach((c) => this.finishLoadingForComponent(c));
    this.spinnerActiveDivs = [];
    this.checkActive();
    this.updateSpinnerType();
    this.spinnerText$.next('');
  }

  get spinnerText(): Observable<string> {
    return this.spinnerText$.asObservable();
  }

  get isActive(): Observable<boolean> {
    return this.isActive$.asObservable();
  }

  get currentSpinnerType(): Observable<SpinnerType> {
    return this.currentSpinnerType$.asObservable();
  }

  private checkActive(): void {
    const isNotLoginPage = this.router.url !== '/login';
    const isNotZeroActiveRequests = this.activeRequests !== 0;
    this.isActive$.next(isNotZeroActiveRequests && isNotLoginPage);
  }

  private updateSpinnerType(): void {
    // priority order: check for custom spinner types first, then default
    for (const [type, count] of this.activeRequestsByType.entries()) {
      if (count > 0 && type !== SpinnerType.DEFAULT) {
        this.currentSpinnerType$.next(type);
        return;
      }
    }
    this.currentSpinnerType$.next(SpinnerType.DEFAULT);
  }
}
